import * as i0 from '@angular/core';
import { EventEmitter, Component, ViewEncapsulation, Output, Input, ViewChild, ElementRef, Injectable, NgModule } from '@angular/core';
import videojs from 'video.js';
import 'webrtc-adapter/out/adapter';
import 'videojs-record';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1$1 from 'ngx-bootstrap/component-loader';
import { ComponentLoaderFactory } from 'ngx-bootstrap/component-loader';
import { PositioningService } from 'ngx-bootstrap/positioning';

class VideoRecorderComponent {
    constructor() {
        this._isRecorded = false;
        this._isRecorderReady = false;
        this.playerReady = new EventEmitter();
        this.recordReady = new EventEmitter();
        this.recordFinished = new EventEmitter();
        this.videoRecordedReady = new EventEmitter();
        this.destroy = new EventEmitter();
        this.labelCameraUnavailable = 'camera unavailable';
        this.resolutions = {
            low: { width: 640, height: 360 },
            regular: { width: 1280, height: 720 },
            high: { width: 1920, height: 1080 }
        };
        this.currentResolution = this.resolutions.low;
        this.defaultOptions = {
            fluid: true,
            aspectRatio: '16:9',
            controls: false,
            autoplay: false,
            loop: false,
            width: Number(this.currentResolution.width),
            height: Number(this.currentResolution.height),
            plugins: {
                record: {
                    audio: true,
                    video: {
                        width: this.currentResolution.width,
                        height: this.currentResolution.height
                    },
                    maxLength: 200,
                    debug: true,
                    frameWidth: Number(this.currentResolution.width),
                    frameHeight: Number(this.currentResolution.height)
                }
            }
        };
    }
    get options() {
        return this._options;
    }
    get recorder() {
        return this.player.record();
    }
    get isRecorded() {
        return this._isRecorded;
    }
    get isRecorderReady() {
        return this._isRecorderReady;
    }
    get isPlayerReady() {
        return this._isPlayerReady;
    }
    get isPlayerEnded() {
        return (this.player && this.player.ended()) || false;
    }
    set options(options) {
        this._options = { ...this.defaultOptions, ...options };
    }
    ngAfterViewInit() {
        this.player = videojs(this.container.nativeElement, this.options, () => {
            this.player.on('contextmenu', e => {
                e.preventDefault();
            });
            this.player.on('finishRecord', () => {
                this._isRecorded = true;
                this.recordFinished.emit(this.getRecordedData());
                this.player.one('loadstart', () => {
                    this.videoRecordedReady.emit();
                });
            });
            this.player.on('deviceReady', () => {
                this._isRecorderReady = true;
                this.recordReady.emit();
            });
            this.playerReady.emit();
            this._isPlayerReady = true;
        });
    }
    getRecordedData() {
        return this.player.recordedData;
    }
    startRecorded() {
        this.player.play();
    }
    getRecordType() {
        return this.recorder.getRecordType();
    }
    pauseRecorded() {
        this.player.pause();
    }
    resetRecorded() {
        this.player.pause();
        this.player.currentTime(0);
    }
    startRecord() {
        this.recorder.start();
        this._isRecorded = false;
    }
    stopRecord() {
        this.recorder.stop();
    }
    saveRecorded(name, extension = 'webm') {
        this.recorder.saveAs({
            video: name + '.' + extension
        });
    }
    reset() {
        this._isRecorded = false;
        this._isRecorderReady = false;
        this.recorder.reset();
    }
    pauseRecord() {
        this.recorder.pause();
    }
    resumeRecord() {
        this.recorder.resume();
    }
    startDevice() {
        if (this.isPlayerReady) {
            this.recorder.reset();
            this.recorder.getDevice();
        }
        else {
            const subscription = this.playerReady.subscribe(() => {
                this.recorder.getDevice();
                subscription.unsubscribe();
            });
        }
    }
    stopDevice() {
        this.recorder.stopDevice();
        this._isRecorderReady = false;
    }
    getRecordedTime() {
        return this.recorder.getDuration();
    }
    displayVideoPreview() {
        if (this.getRecordedTime() > 0) {
            this.startRecorded();
        }
    }
    ngOnDestroy() {
        if (this.player) {
            this.player.dispose();
        }
        this.destroy.emit();
    }
}
VideoRecorderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: VideoRecorderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
VideoRecorderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.8", type: VideoRecorderComponent, selector: "cc-video-recorder", inputs: { options: "options", labelCameraUnavailable: "labelCameraUnavailable" }, outputs: { playerReady: "playerReady", recordReady: "recordReady", recordFinished: "recordFinished", videoRecordedReady: "videoRecordedReady", destroy: "destroy" }, viewQueries: [{ propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"recorder-not-ready\" *ngIf=\"!isRecorderReady\">{{ labelCameraUnavailable}}</div>\r\n<div data-vjs-player>\r\n  <video #container class=\"video-js vjs-16-9 vjs-default-skin\"></video>\r\n</div>", styles: ["cc-video-recorder{position:relative;display:block;overflow:hidden;border-radius:.5rem}.recorder-not-ready{position:absolute;inset:0;width:100%;height:100%;background-color:#1f363f;color:#699bb1;font-size:20px;font-weight:700;z-index:2;display:flex;justify-content:center;flex-direction:column;text-align:center}.vjs-device-button{display:none!important}.play-button{position:absolute;cursor:pointer;left:calc(50% - 28px);top:calc(50% - 28px);z-index:20;width:56px;height:56px;background-repeat:no-repeat;background-size:contain;background-image:url(data:image/svg+xml;base64,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)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: VideoRecorderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cc-video-recorder', encapsulation: ViewEncapsulation.None, template: "<div class=\"recorder-not-ready\" *ngIf=\"!isRecorderReady\">{{ labelCameraUnavailable}}</div>\r\n<div data-vjs-player>\r\n  <video #container class=\"video-js vjs-16-9 vjs-default-skin\"></video>\r\n</div>", styles: ["cc-video-recorder{position:relative;display:block;overflow:hidden;border-radius:.5rem}.recorder-not-ready{position:absolute;inset:0;width:100%;height:100%;background-color:#1f363f;color:#699bb1;font-size:20px;font-weight:700;z-index:2;display:flex;justify-content:center;flex-direction:column;text-align:center}.vjs-device-button{display:none!important}.play-button{position:absolute;cursor:pointer;left:calc(50% - 28px);top:calc(50% - 28px);z-index:20;width:56px;height:56px;background-repeat:no-repeat;background-size:contain;background-image:url(data:image/svg+xml;base64,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)}\n"] }]
        }], propDecorators: { playerReady: [{
                type: Output
            }], recordReady: [{
                type: Output
            }], recordFinished: [{
                type: Output
            }], videoRecordedReady: [{
                type: Output
            }], destroy: [{
                type: Output
            }], options: [{
                type: Input
            }], labelCameraUnavailable: [{
                type: Input
            }], container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }] } });

class VideoRecorderService {
    constructor(clf) {
        this.clf = clf;
        this.recorders = {};
    }
    init(name, attachTo, options = {}) {
        let recorder = this.recorders[name];
        if (recorder) {
            this.attachTo(name, attachTo);
            if (recorder.instance.isRecorderReady) {
                recorder.instance.recordReady.emit();
            }
            return recorder.instance;
        }
        recorder = this.clf
            .createLoader(null, null, null)
            .attach(VideoRecorderComponent)
            .to(attachTo);
        recorder.show({ options: options });
        this.recorders[name] = recorder;
        return recorder.instance;
    }
    get(name) {
        const recorder = this.recorders[name];
        return recorder.instance;
    }
    attachTo(name, attachTo) {
        const recorder = this.recorders[name];
        if (!recorder) {
            throw new Error(`Recorder ${name} not found`);
        }
        recorder.to(attachTo);
        if (attachTo instanceof ElementRef) {
            attachTo.nativeElement.appendChild(recorder._componentRef.location.nativeElement);
        }
        if (attachTo === 'body' && typeof document !== 'undefined') {
            document.querySelector(attachTo).appendChild(recorder._componentRef.location.nativeElement);
        }
        return recorder.instance;
    }
}
VideoRecorderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: VideoRecorderService, deps: [{ token: i1$1.ComponentLoaderFactory }], target: i0.ɵɵFactoryTarget.Injectable });
VideoRecorderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: VideoRecorderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: VideoRecorderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.ComponentLoaderFactory }]; } });

class VideoRecorderModule {
}
VideoRecorderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: VideoRecorderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
VideoRecorderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.8", ngImport: i0, type: VideoRecorderModule, declarations: [VideoRecorderComponent], imports: [CommonModule], exports: [VideoRecorderComponent] });
VideoRecorderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: VideoRecorderModule, providers: [ComponentLoaderFactory, PositioningService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.8", ngImport: i0, type: VideoRecorderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [VideoRecorderComponent],
                    providers: [ComponentLoaderFactory, PositioningService],
                    imports: [
                        CommonModule
                    ],
                    exports: [VideoRecorderComponent]
                }]
        }] });

/*
 * Public API Surface of video-recorder
 */

/**
 * Generated bundle index. Do not edit.
 */

export { VideoRecorderComponent, VideoRecorderModule, VideoRecorderService };
